<template>
  <div
    ref="popupElement"
    data-ht-module="broken-cart-popup"
    class="Cart__BrokenPopup"
    :class="{
      'Cart__BrokenPopup--Active': isActive,
      'Cart__BrokenPopup--Hidden': hasClosed,
    }"
    role="dialog"
    aria-labelledby="BrokenCartPopupHeading"
    aria-describedby="BrokenCartPopupCopy"
    tabindex="-1"
    :inert="!isOpen"
    @keydown.esc="hidePopup"
  >
    <button
      type="button"
      class="Cart__BrokenPopup--Close"
      aria-label="Close"
      data-ht-element="broken-cart-popup-close"
      @click="hidePopup"
    >
      <Icon :icon="'close'" :aria-hidden="true" />
    </button>
    <p
      id="BrokenCartPopupHeading"
      class="t4 Cart__BrokenPopup--Heading"
      data-color="night-sky-dark"
    >
      {{ store.terms.broken_cart.title }}
    </p>
    <p
      id="BrokenCartPopupCopy"
      class="p5 Cart__BrokenPopup--Copy"
      data-color="night-sky-dark"
    >
      {{ store.terms.broken_cart.copy }}
    </p>

    <div class="Cart__BrokenPopup--Buttons">
      <a
        class="button button--secondary"
        href="/pages/cart-support"
        data-ht-element="broken-cart-popup-contact-support-cta"
      >
        {{ store.terms.broken_cart.button_1 }}
      </a>
      <button
        class="button button--primary"
        data-ht-element="broken-cart-popup-review-cart-cta"
        @click="hidePopup"
      >
        {{ store.terms.broken_cart.button_2 }}
      </button>
    </div>

    <label class="p5">
      <input
        data-ht-element="broken-cart-popup-checkbox"
        type="checkbox"
        name="BrokenCart_Popup"
        @change="toggleCookie"
      />
      <span class="BrokenCart_Popup--Disclaimer" data-color="night-sky-dark">{{
        store.terms.broken_cart.disclaimer
      }}</span>
    </label>
  </div>
</template>

<script setup>
import { useCartStore } from './Composables/Store';
import Cookies from 'js-cookie';
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import Icon from '../Global/Icon.vue';
import { trapFocus } from '@utils/global-functions';

const popupElement = ref(null);
const hasClosed = ref(false);
const initialState = ref(false);
let previouslyFocusedElement = null;
const hidePopup = () => {
  hasClosed.value = true;
};

const toggleCookie = (value) => {
  if (value.target.checked) {
    Cookies.set('_bl_hide_broken_cart_popup', true, {
      expires: 21,
    });
  } else {
    Cookies.remove('_bl_hide_broken_cart_popup', false);
  }
};
const store = useCartStore();

const isActive = computed(() =>
  initialState.value ? store.hasOrphanedBundleItems : false
);
const isOpen = computed(() => isActive.value && !hasClosed.value);

const isCartPage = window.location.pathname == '/cart';

/**
 * Moves focus into the popup when it opens so keyboard and screen reader
 * users land on it, and returns focus to where they were when it closes.
 * On the cart page, focus is also trapped inside the popup. The cart drawer
 * already traps focus itself, so nesting a second trap there is skipped.
 */
watch(isOpen, (open) => {
  if (open) {
    previouslyFocusedElement = document.activeElement;
    // wait for the DOM patch to drop `inert` — focus() on an inert element is a no-op
    nextTick(() => {
      if (!popupElement.value) return;
      if (isCartPage) {
        trapFocus(popupElement.value);
      } else {
        popupElement.value.focus();
      }
    });
  } else if (popupElement.value?.contains(document.activeElement)) {
    previouslyFocusedElement?.focus();
  }
});

onMounted(async () => {
  if (Cookies.get('_bl_hide_broken_cart_popup')) {
    hasClosed.value = true;
  }

  //if viewing on the cart page, initialize popup immediately if applicable and ignore overlay event listeners
  if (isCartPage) {
    setTimeout(() => {
      initialState.value = true;
    }, 1000);
  } else {
    window.addEventListener('init-overlay', (e) => {
      if (e.detail == 'cart') {
        setTimeout(() => {
          initialState.value = true;
        }, 1000);
      }
    });
    window.addEventListener('close-overlay', (e) => {
      if (e.detail == 'cart') {
        if (!Cookies.get('_bl_hide_broken_cart_popup')) {
          hasClosed.value = false;
        }
      }
    });
  }
});
</script>
